import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { api, formatCurrency } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Package, Sparkles, Users2, Check, X as XIcon, Loader2, CloudUpload, FileText, Eye } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth, isAdmin } from '@/contexts/AuthContext';
import { cloudinaryPdfPreview, cloudinaryOptimizedImage, isCloudinaryUrl, extractPublicId } from '@/lib/cloudinary';

const empty = { company_id: '', company_ids: [], category: 'fotovoltaico_accumulo', brand: '', model: '', description: '', power: '', unit_price: 0, notes: '', active: true, is_custom: false };

export default function Products() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [options, setOptions] = useState({ product_categories: [], cloudinary_cloud_name: '' });
  const { user: me } = useAuth();
  const [migrating, setMigrating] = useState(false);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all | active | inactive
  const [filterPhase, setFilterPhase] = useState('all'); // all | monofase | trifase
  const [powerMin, setPowerMin] = useState('');
  const [powerMax, setPowerMax] = useState('');
  const [kwhMin, setKwhMin] = useState('');
  const [kwhMax, setKwhMax] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set()); // bulk selection
  const [bulkOpen, setBulkOpen] = useState(false);           // bulk dialog visibility
  const [bulkAdd, setBulkAdd] = useState(new Set());
  const [bulkRemove, setBulkRemove] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [prods, comps, opts] = await Promise.all([api.listProducts(), api.listCompanies(), api.getOptions()]);
      setItems(prods); setCompanies(comps); setOptions(opts);
    } catch (e) { toast.error('Errore caricamento'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const companiesMap = useMemo(() => Object.fromEntries(companies.map(c => [c.id, c])), [companies]);
  const catMap = useMemo(() => Object.fromEntries((options.product_categories || []).map(c => [c.key, c.label])), [options]);

  // Estrae valore numerico "potenza" (kW/kWp/kWh/Wp) dal campo power per confronti min/max
  const parseNumericPower = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim().replace(',', '.');
    const m = s.match(/([\d.]+)/);
    if (!m) return null;
    let n = parseFloat(m[1]);
    if (Number.isNaN(n)) return null;
    if (/wp\b|watt/i.test(s) && !/kwp?\b/i.test(s)) n = n / 1000;
    return n;
  };

  const brandOptions = useMemo(() => {
    const set = new Set();
    items.forEach(p => { const b = (p.brand || '').trim(); if (b) set.add(b); });
    return [...set].sort((a, b) => a.localeCompare(b, 'it'));
  }, [items]);

  const filtered = items.filter(p => {
    if (filterCompany !== 'all') {
      const ids = p.company_ids && p.company_ids.length > 0 ? p.company_ids : [p.company_id];
      if (!ids.includes(filterCompany)) return false;
    }
    if (filterCategory !== 'all' && p.category !== filterCategory) return false;
    if (filterBrand !== 'all' && (p.brand || '').trim() !== filterBrand) return false;
    if (filterStatus === 'active' && !p.active) return false;
    if (filterStatus === 'inactive' && p.active) return false;
    if (filterPhase !== 'all' && p.phase_type && p.phase_type !== filterPhase) return false;
    if (filterPhase !== 'all' && !p.phase_type) return false;
    const pw = parseNumericPower(p.power);
    if (powerMin !== '') {
      const min = parseFloat(String(powerMin).replace(',', '.'));
      if (!Number.isNaN(min) && (pw == null || pw < min)) return false;
    }
    if (powerMax !== '') {
      const max = parseFloat(String(powerMax).replace(',', '.'));
      if (!Number.isNaN(max) && (pw == null || pw > max)) return false;
    }
    const kwh = Number(p.battery_kwh) || 0;
    if (kwhMin !== '') {
      const min = parseFloat(String(kwhMin).replace(',', '.'));
      if (!Number.isNaN(min) && kwh < min) return false;
    }
    if (kwhMax !== '') {
      const max = parseFloat(String(kwhMax).replace(',', '.'));
      if (!Number.isNaN(max) && kwh > max) return false;
    }
    const price = Number(p.unit_price) || 0;
    if (priceMin !== '') {
      const min = parseFloat(String(priceMin).replace(',', '.'));
      if (!Number.isNaN(min) && price < min) return false;
    }
    if (priceMax !== '') {
      const max = parseFloat(String(priceMax).replace(',', '.'));
      if (!Number.isNaN(max) && price > max) return false;
    }
    if (search) {
      const t = search.toLowerCase();
      if (![p.brand, p.model, p.description].some(s => (s || '').toLowerCase().includes(t))) return false;
    }
    return true;
  });

  const activeFiltersCount =
    (filterCompany !== 'all' ? 1 : 0) +
    (filterCategory !== 'all' ? 1 : 0) +
    (filterBrand !== 'all' ? 1 : 0) +
    (filterStatus !== 'all' ? 1 : 0) +
    (filterPhase !== 'all' ? 1 : 0) +
    (powerMin !== '' ? 1 : 0) +
    (powerMax !== '' ? 1 : 0) +
    (kwhMin !== '' ? 1 : 0) +
    (kwhMax !== '' ? 1 : 0) +
    (priceMin !== '' ? 1 : 0) +
    (priceMax !== '' ? 1 : 0) +
    (search ? 1 : 0);

  const resetFilters = () => {
    setFilterCompany('all'); setFilterCategory('all'); setFilterBrand('all');
    setFilterStatus('all'); setFilterPhase('all'); setPowerMin(''); setPowerMax('');
    setKwhMin(''); setKwhMax(''); setPriceMin(''); setPriceMax(''); setSearch('');
    setPage(1);
  };

  // Reset page quando cambiano filtri o dataset
  useEffect(() => { setPage(1); }, [
    filterCompany, filterCategory, filterBrand, filterStatus, filterPhase,
    powerMin, powerMax, kwhMin, kwhMax, priceMin, priceMax, search, items.length,
  ]);

  // Paginazione (max 20 per pagina)
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleNew = () => {
    if (companies.length === 0) { toast.error('Crea prima una azienda partner'); return; }
    setEditId(null);
    setForm({ ...empty, company_id: filterCompany !== 'all' ? filterCompany : companies[0].id, category: filterCategory !== 'all' ? filterCategory : 'fotovoltaico_accumulo' });
    setOpen(true);
  };
  const handleNewCustom = () => {
    if (companies.length === 0) { toast.error('Crea prima una azienda partner'); return; }
    setEditId(null);
    setForm({
      ...empty,
      company_id: filterCompany !== 'all' ? filterCompany : companies[0].id,
      category: 'accessorio',
      model: 'Voce personalizzata',
      description: 'Descrizione modificabile in fase di preventivo/contratto',
      is_custom: true,
    });
    setOpen(true);
  };
  const handleEdit = (p) => { setEditId(p.id); setForm({ ...empty, ...p }); setOpen(true); };

  const handleSave = async () => {
    if (!form.company_id) { toast.error('Seleziona un\'azienda partner'); return; }
    if (!form.description?.trim() && !form.model?.trim() && !form.brand?.trim()) { toast.error('Inserisci almeno marca, modello o descrizione'); return; }
    try {
      const payload = { ...form, unit_price: Number(form.unit_price) || 0 };
      if (editId) { await api.updateProduct(editId, payload); toast.success('Prodotto aggiornato'); }
      else { await api.createProduct(payload); toast.success('Prodotto creato'); }
      setOpen(false); setEditId(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const handleDelete = async (id) => { try { await api.deleteProduct(id); toast.success('Eliminato'); load(); } catch { toast.error('Errore eliminazione'); } };

  // ----- Bulk selection helpers -----
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const visibleIds = pagedItems.map(p => p.id);
    setSelectedIds(prev => {
      const allSelected = visibleIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const openBulkDialog = () => {
    // Precompila con l'intersezione dei company_ids (aziende già abilitate su TUTTI i selezionati)
    const selectedProducts = items.filter(p => selectedIds.has(p.id));
    if (selectedProducts.length === 0) return;
    setBulkAdd(new Set());
    setBulkRemove(new Set());
    setBulkOpen(true);
  };
  const applyBulk = async () => {
    if (bulkAdd.size === 0 && bulkRemove.size === 0) { toast.error('Seleziona almeno un\'azienda da abilitare o disabilitare'); return; }
    setBulkSaving(true);
    try {
      const res = await api.bulkEnableCompaniesOnProducts({
        product_ids: Array.from(selectedIds),
        add_company_ids: Array.from(bulkAdd),
        remove_company_ids: Array.from(bulkRemove),
      });
      toast.success(`${res.updated} prodotti aggiornati su ${res.matched}`);
      if (res.warnings && res.warnings.length) {
        res.warnings.slice(0, 3).forEach(w => toast.warning(w));
      }
      setBulkOpen(false);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore azione bulk');
    } finally { setBulkSaving(false); }
  };
  const toggleBulk = (setter, id) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div>
      <PageHeader
        title="Catalogo Prodotti"
        description="Prodotti e soluzioni di efficientamento energetico configurabili per ogni azienda partner"
        actions={
          <div className="flex gap-2 flex-wrap">
            {isAdmin(me) && (
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Migrare tutte le schede tecniche e miniature ancora in base64 su Cloudinary?\nOperazione idempotente: i prodotti già migrati saranno ignorati.')) return;
                  setMigrating(true);
                  try {
                    const r = await api.migrateProductsToCloudinary({ dry_run: false });
                    toast.success(`Migrazione completata: ${r.migrated_count} migrati, ${r.skipped_count} saltati, ${r.failed_count} falliti`);
                    if (r.failed_count > 0) console.warn('Migration failures:', r.failed);
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Migrazione fallita');
                  } finally { setMigrating(false); }
                }}
                disabled={migrating}
                data-testid="products-migrate-cloudinary"
                title="Sposta su Cloudinary tutti i media dei prodotti ancora in base64"
              >
                {migrating ? <Loader2 className="size-4 mr-2 animate-spin" /> : <CloudUpload className="size-4 mr-2" />}
                {migrating ? 'Migrazione...' : 'Migra a Cloudinary'}
              </Button>
            )}
            {isAdmin(me) && (
              <Button
                variant="outline"
                onClick={async () => {
                  const msg = 'Ri-carica su Cloudinary come RAW verificato tutti i PDF che attualmente hanno un URL con delivery bloccata (tipicamente vecchi upload come "image").\n\n⚠ Operazione potenzialmente lunga: scansiona tutti i prodotti, scarica ogni PDF con delivery bloccata, lo ri-carica come raw, e sostituisce l\'URL nel DB solo se la verifica passa.\n\nProcedere?';
                  if (!window.confirm(msg)) return;
                  setMigrating(true);
                  try {
                    const r = await api.migrateProductsToCloudinary({ dry_run: false, reupload_bad_urls: true });
                    const detail = `${r.migrated_count} elaborati · ${r.reuploaded_count || 0} PDF ri-caricati come raw · ${r.skipped_count} saltati · ${r.failed_count} falliti`;
                    if (r.failed_count > 0) {
                      toast.warning('Ri-migrazione completata con avvisi', { description: detail, duration: 12000 });
                      console.warn('Reupload failures:', r.failed);
                    } else {
                      toast.success('Ri-migrazione completata', { description: detail });
                    }
                    if (r.reuploaded_samples?.length) console.info('Reuploaded samples:', r.reuploaded_samples);
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Ri-migrazione fallita');
                  } finally { setMigrating(false); }
                }}
                disabled={migrating}
                data-testid="products-reupload-cloudinary"
                title="Ri-carica come raw verificato i PDF Cloudinary con delivery bloccata (image → raw)"
              >
                {migrating ? <Loader2 className="size-4 mr-2 animate-spin" /> : <CloudUpload className="size-4 mr-2" />}
                Rigenera URL rotti
              </Button>
            )}
            <Button variant="outline" onClick={handleNewCustom} data-testid="products-create-custom"><Sparkles className="size-4 mr-2" />Voce personalizzabile</Button>
            <Button onClick={handleNew} data-testid="products-create-button"><Plus className="size-4 mr-2" />Nuovo prodotto</Button>
          </div>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
              <Input placeholder="Cerca marca, modello, descrizione..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="products-search-input" />
              <Select value={filterCompany} onValueChange={setFilterCompany}>
                <SelectTrigger className="w-full md:w-[200px]" data-testid="products-filter-company"><SelectValue placeholder="Tutte le aziende" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le aziende</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full md:w-[200px]" data-testid="products-filter-category"><SelectValue placeholder="Tutte le categorie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {(options.product_categories || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterBrand} onValueChange={setFilterBrand}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="products-filter-brand"><SelectValue placeholder="Tutte le marche" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le marche</SelectItem>
                  {brandOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-[160px]" data-testid="products-filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="active">Solo attivi</SelectItem>
                  <SelectItem value="inactive">Solo disattivati</SelectItem>
                </SelectContent>
              </Select>
              <div className="inline-flex rounded-lg border p-0.5 bg-secondary/30 h-9" data-testid="products-filter-phase">
                {[
                  { key: 'all', label: 'Tutte' },
                  { key: 'monofase', label: 'Monofase' },
                  { key: 'trifase', label: 'Trifase' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFilterPhase(opt.key)}
                    className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${filterPhase === opt.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`products-filter-phase-${opt.key}`}
                  >{opt.label}</button>
                ))}
              </div>
              <div className="flex-1" />
              <div className="text-xs text-muted-foreground">
                {filtered.length} prodotti · pag. {currentPage}/{totalPages}
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground shrink-0">kWp min</Label>
                <Input type="number" step="0.1" value={powerMin} onChange={(e) => setPowerMin(e.target.value)} placeholder="es. 3" className="w-20" data-testid="products-filter-power-min" />
                <Label className="text-xs text-muted-foreground shrink-0">max</Label>
                <Input type="number" step="0.1" value={powerMax} onChange={(e) => setPowerMax(e.target.value)} placeholder="es. 10" className="w-20" data-testid="products-filter-power-max" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground shrink-0">kWh min</Label>
                <Input type="number" step="0.1" value={kwhMin} onChange={(e) => setKwhMin(e.target.value)} placeholder="es. 5" className="w-20" data-testid="products-filter-kwh-min" />
                <Label className="text-xs text-muted-foreground shrink-0">max</Label>
                <Input type="number" step="0.1" value={kwhMax} onChange={(e) => setKwhMax(e.target.value)} placeholder="es. 15" className="w-20" data-testid="products-filter-kwh-max" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground shrink-0">€ min</Label>
                <Input type="number" step="10" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="es. 500" className="w-24" data-testid="products-filter-price-min" />
                <Label className="text-xs text-muted-foreground shrink-0">max</Label>
                <Input type="number" step="10" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="es. 5000" className="w-24" data-testid="products-filter-price-max" />
              </div>
              <div className="flex-1" />
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="products-filters-reset">
                  <XIcon className="size-3.5 mr-1" />Reset filtri ({activeFiltersCount})
                </Button>
              )}
            </div>
          </div>

          {/* ----- Bulk selection toolbar (visible only when at least 1 selected) ----- */}
          {selectedIds.size > 0 && (
            <div className="px-4 py-2.5 border-b bg-primary/5 flex flex-wrap items-center gap-3" data-testid="products-bulk-toolbar">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Check className="size-4 text-primary" />
                <span data-testid="products-bulk-selected-count">{selectedIds.size}</span> {selectedIds.size === 1 ? 'prodotto selezionato' : 'prodotti selezionati'}
              </div>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={clearSelection} data-testid="products-bulk-clear">
                <XIcon className="size-3.5 mr-1" />Deseleziona
              </Button>
              <Button size="sm" onClick={openBulkDialog} data-testid="products-bulk-enable-btn">
                <Users2 className="size-3.5 mr-1" />Abilita/disabilita aziende
              </Button>
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="p-10 text-center">
              <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><Package className="size-5 text-muted-foreground" /></div>
              <div className="text-sm font-medium">{loading ? 'Caricamento...' : 'Nessun prodotto'}</div>
              {!loading && companies.length > 0 && <div className="text-xs text-muted-foreground mb-4">Aggiungi il primo prodotto al catalogo</div>}
              {!loading && companies.length === 0 && <div className="text-xs text-muted-foreground mb-4">Devi prima creare un’azienda partner</div>}
              {!loading && companies.length > 0 && <Button onClick={handleNew} size="sm"><Plus className="size-4 mr-2" />Nuovo prodotto</Button>}
            </div>
          ) : (
            <Table data-testid="products-table">
              <TableHeader><TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={pagedItems.length > 0 && pagedItems.every(p => selectedIds.has(p.id))}
                    onCheckedChange={toggleSelectAll}
                    data-testid="products-select-all"
                    aria-label="Seleziona tutti"
                  />
                </TableHead>
                <TableHead>Categoria</TableHead><TableHead>Marca / Modello</TableHead><TableHead>Descrizione</TableHead>
                <TableHead className="w-14 text-center">Scheda</TableHead>
                <TableHead>Partner</TableHead><TableHead className="text-right">Prezzo</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {pagedItems.map(p => (
                  <TableRow key={p.id} data-testid={`product-row-${p.id}`} data-selected={selectedIds.has(p.id) || undefined} className={selectedIds.has(p.id) ? 'bg-primary/5' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(p.id)}
                        onCheckedChange={() => toggleSelect(p.id)}
                        data-testid={`product-select-${p.id}`}
                        aria-label={`Seleziona ${p.brand} ${p.model}`}
                      />
                    </TableCell>
                    <TableCell><Badge variant="secondary">{catMap[p.category] || p.category}</Badge></TableCell>
                    <TableCell>
                      <div className="font-medium flex items-center gap-1.5">{p.brand} {p.model}
                        {p.is_custom && <Badge variant="outline" className="gap-1 text-[10px] border-accent text-accent"><Sparkles className="size-3" />Personalizzabile</Badge>}
                        {p.sales_zone && <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{p.sales_zone}</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{p.power}{p.phase_type ? ` · ${p.phase_type}` : ''}{p.battery_kwh ? ` · ${p.battery_kwh} kWh` : ''}</div>
                    </TableCell>
                    <TableCell className="max-w-sm truncate text-sm">{p.description}</TableCell>
                    <TableCell>
                      {(() => {
                        const cloud = options.cloudinary_cloud_name;
                        // Anteprima: prima priorità = thumbnail Cloudinary; poi 1° datasheet PDF Cloudinary; poi legacy datasheet
                        const thumbPid = p.thumbnail_public_id;
                        const firstDs = (p.datasheets && p.datasheets[0]) || null;
                        const dsPid = firstDs?.public_id || p.datasheet_public_id || (isCloudinaryUrl(firstDs?.data_url) ? extractPublicId(firstDs.data_url) : (isCloudinaryUrl(p.datasheet_data_url) ? extractPublicId(p.datasheet_data_url) : ''));
                        const previewUrl = thumbPid
                          ? cloudinaryOptimizedImage(cloud, thumbPid, { width: 60 })
                          : (dsPid && cloud ? cloudinaryPdfPreview(cloud, dsPid, { width: 60 }) : '');
                        const openUrl = (firstDs?.data_url) || p.datasheet_data_url || p.thumbnail_data_url || '';
                        if (previewUrl) {
                          return (
                            <a
                              href={openUrl || previewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block size-10 rounded-md overflow-hidden border bg-secondary/40 hover:ring-2 hover:ring-primary/40 transition"
                              title="Apri scheda tecnica"
                              data-testid={`product-preview-${p.id}`}
                            >
                              <img src={previewUrl} alt="preview" className="size-full object-cover" loading="lazy" />
                            </a>
                          );
                        }
                        if (openUrl) {
                          return (
                            <a href={openUrl} target="_blank" rel="noopener noreferrer" className="inline-flex size-10 rounded-md border bg-secondary/40 items-center justify-center text-muted-foreground hover:text-primary" title="Apri scheda tecnica (legacy)" data-testid={`product-preview-${p.id}`}>
                              <FileText className="size-4" />
                            </a>
                          );
                        }
                        return <span className="text-muted-foreground text-xs">—</span>;
                      })()}
                    </TableCell>
                    <TableCell className="text-sm">
                      {(() => {
                        const primaryName = companiesMap[p.company_id]?.name || '—';
                        const extraIds = (p.company_ids || []).filter(cid => cid && cid !== p.company_id);
                        return (
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge variant="secondary" className="text-[10px]">{primaryName}</Badge>
                            {extraIds.map(cid => (
                              <Badge key={cid} variant="outline" className="text-[10px]" data-testid={`product-partner-badge-${cid}`}>
                                {companiesMap[cid]?.name || cid.slice(0, 8)}
                              </Badge>
                            ))}
                            {extraIds.length > 0 && <span className="text-[10px] text-muted-foreground">+{extraIds.length} abilitat{extraIds.length === 1 ? 'a' : 'e'}</span>}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatCurrency(p.unit_price)}</TableCell>
                    <TableCell>{p.active ? <Badge>Attivo</Badge> : <Badge variant="outline">Inattivo</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(p)} data-testid={`product-edit-${p.id}`}><Pencil className="size-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`product-delete-${p.id}`}><Trash2 className="size-4 text-destructive" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Eliminare il prodotto?</AlertDialogTitle><AlertDialogDescription>L’azione non può essere annullata.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(p.id)}>Elimina</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {totalPages > 1 && (
            <div className="p-3 border-t flex items-center justify-between gap-2 flex-wrap" data-testid="products-pagination">
              <div className="text-xs text-muted-foreground">
                Mostrando {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filtered.length)} di {filtered.length}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage(1)} data-testid="products-page-first">«</Button>
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="products-page-prev">‹ Precedente</Button>
                <span className="px-3 text-sm tabular-nums" data-testid="products-page-current">Pag. {currentPage} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="products-page-next">Successiva ›</Button>
                <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)} data-testid="products-page-last">»</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Modifica prodotto' : 'Nuovo prodotto'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Azienda partner principale *</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="product-company-select"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">L&apos;azienda proprietaria del prodotto.</p>
            </div>
            <div className="space-y-2"><Label>Categoria *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(options.product_categories || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Aziende partner aggiuntive abilitate a vendere questo prodotto</Label>
              <div className="rounded-lg border p-3 max-h-40 overflow-y-auto space-y-1.5 bg-secondary/20" data-testid="product-extra-companies">
                {companies.filter(c => c.id !== form.company_id).length === 0 ? (
                  <p className="text-xs text-muted-foreground">Non ci sono altre aziende partner disponibili.</p>
                ) : companies.filter(c => c.id !== form.company_id).map(c => {
                  const isChecked = (form.company_ids || []).includes(c.id);
                  return (
                    <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-secondary/40 rounded px-2 py-1 -mx-1">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        className="accent-primary size-4"
                        data-testid={`product-enable-partner-${c.id}`}
                        onChange={(e) => {
                          const next = new Set(form.company_ids || []);
                          if (e.target.checked) next.add(c.id); else next.delete(c.id);
                          setForm({ ...form, company_ids: Array.from(next) });
                        }}
                      />
                      <span className="text-sm">{c.name}</span>
                      {isChecked && <Badge variant="outline" className="ml-auto text-[10px]">abilitata</Badge>}
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Spunta le altre aziende che potranno vedere e vendere questo stesso prodotto. Utile per condividere accessori, servizi o schede tecniche comuni tra più partner.
              </p>
            </div>
            <div className="space-y-2"><Label>Marca</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="product-brand-input" /></div>
            <div className="space-y-2"><Label>Modello</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="product-model-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Descrizione</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input" /></div>
            <div className="space-y-2"><Label>Potenza / Capacità</Label><Input value={form.power} onChange={(e) => setForm({ ...form, power: e.target.value })} placeholder="6 kW" /></div>
            <div className="space-y-2"><Label>Prezzo unitario (€) *</Label><Input type="number" step="0.01" min="0" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="tabular-nums" data-testid="product-price-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Note interne</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Prodotto attivo</Label></div>
            <div className="flex items-start gap-3">
              <Switch checked={!!form.is_custom} onCheckedChange={(v) => setForm({ ...form, is_custom: v })} data-testid="product-iscustom-switch" className="mt-1" />
              <div>
                <Label className="cursor-pointer">Voce personalizzabile</Label>
                <p className="text-xs text-muted-foreground">L&apos;operatore potrà modificare descrizione, marca, modello e prezzo in fase di preventivo/contratto.</p>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={handleSave} data-testid="product-save-button">{editId ? 'Salva' : 'Crea'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Bulk enable/disable companies dialog ---- */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="products-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users2 className="size-4 text-primary" />Abilita o disabilita aziende partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-lg border bg-secondary/40 p-3 text-xs">
              L&apos;azione sarà applicata a <strong>{selectedIds.size} prodotti selezionati</strong>. L&apos;azienda proprietaria di ogni prodotto (primary) non può essere rimossa da qui — per eliminare il prodotto usa l&apos;azione Elimina.
            </div>

            <div>
              <div className="text-xs font-medium mb-1.5">Aziende da <span className="text-primary">abilitare</span> (aggiunge)</div>
              <div className="rounded-lg border p-2 max-h-40 overflow-y-auto space-y-1 bg-background">
                {companies.length === 0 && <p className="text-xs text-muted-foreground p-1">Nessuna azienda disponibile.</p>}
                {companies.map(c => (
                  <label key={`add-${c.id}`} className="flex items-center gap-2 cursor-pointer hover:bg-secondary/40 rounded px-2 py-1">
                    <input
                      type="checkbox"
                      className="accent-primary size-4"
                      checked={bulkAdd.has(c.id)}
                      onChange={() => toggleBulk(setBulkAdd, c.id)}
                      data-testid={`products-bulk-add-${c.id}`}
                    />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium mb-1.5">Aziende da <span className="text-destructive">disabilitare</span> (rimuove)</div>
              <div className="rounded-lg border p-2 max-h-40 overflow-y-auto space-y-1 bg-background">
                {companies.length === 0 && <p className="text-xs text-muted-foreground p-1">Nessuna azienda disponibile.</p>}
                {companies.map(c => (
                  <label key={`rem-${c.id}`} className="flex items-center gap-2 cursor-pointer hover:bg-secondary/40 rounded px-2 py-1">
                    <input
                      type="checkbox"
                      className="accent-destructive size-4"
                      checked={bulkRemove.has(c.id)}
                      onChange={() => toggleBulk(setBulkRemove, c.id)}
                      data-testid={`products-bulk-remove-${c.id}`}
                    />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkSaving}>Annulla</Button>
            <Button onClick={applyBulk} disabled={bulkSaving || (bulkAdd.size === 0 && bulkRemove.size === 0)} data-testid="products-bulk-apply">
              {bulkSaving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Check className="size-4 mr-2" />}
              Applica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
